import { CircleCheck, CirclePause, Folder } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useFetcher } from "~/hooks/useFetcher";
import { PreviewRow } from "~/pages/manifold-nextgen/components/PreviewRow";
import { CohortAccessRequest } from "~/pages/manifold-nextgen/components/cohorts/interfaces/access_requests";
import { nextGenDatasetDetailPath } from "~/router/constants";
import {
  type ApprovalStreamStatus,
  ChildDatasetExportPanel,
  type ChildDatasetExportPanelState,
} from "./ChildDatasetExportPanel";

const { VITE_CATALOG_API } = import.meta.env;

// bg-green-700 etc. invert between light and dark themes, which washes out a solid action
// button. This references the primitive token directly so Approve keeps the same fixed color
// in both themes (matching the design system's button-filled-success spec).
export const APPROVE_BUTTON_CLASS =
  "min-w-40 text-white bg-[var(--dt-primitives-color-green-500)] hover:bg-[var(--dt-primitives-color-green-700)]";

const EXPORT_STATUS_LABEL: Record<ApprovalStreamStatus, string> = {
  idle: "Not started",
  streaming: "Exporting…",
  done: "Export complete",
  error: "Export failed",
};

function StepMarker({ step, connectDown }: { step: number; connectDown?: boolean }) {
  return (
    <div className="flex flex-col items-center">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border text-sm font-semibold text-foreground">
        {step}
      </div>
      {connectDown && <div className="my-1 w-px flex-1 bg-border" />}
    </div>
  );
}

interface ChildDatasetApproveRequestModalProps {
  open: boolean;
  request: CohortAccessRequest;
  exportPanel: ChildDatasetExportPanelState;
  onDatasetNameChange: (name: string) => void;
  onExport: () => void;
  onApprove: () => void;
  onCancel: () => void;
  isApproving: boolean;
}

export function ChildDatasetApproveRequestModal({
  open,
  request,
  exportPanel,
  onDatasetNameChange,
  onExport,
  onApprove,
  onCancel,
  isApproving,
}: ChildDatasetApproveRequestModalProps) {
  const isExportStreaming = exportPanel.streamStatus === "streaming";
  const isReadyToGrant =
    exportPanel.streamStatus === "done" && Boolean(exportPanel.resultDatasetId);

  const { data: exportedDataset, error: exportedDatasetError } = useFetcher<{ title: string }>(
    exportPanel.resultDatasetId
      ? `${VITE_CATALOG_API}/datasets/${exportPanel.resultDatasetId}`
      : null
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isExportStreaming && onCancel()}>
      <DialogContent onCloseButtonClick={onCancel} className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Approve this request</DialogTitle>
          <DialogDescription>
            Export the dataset, then confirm approval to grant access.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4">
          <StepMarker step={1} connectDown />
          <div className="flex-1 space-y-3 pb-6">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-foreground">Export the dataset</p>
              {exportPanel.streamStatus === "done" ? (
                <Badge variant="success">
                  <CircleCheck aria-hidden />
                  {EXPORT_STATUS_LABEL.done}
                </Badge>
              ) : exportPanel.streamStatus === "idle" ? (
                <Badge variant="muted">{EXPORT_STATUS_LABEL.idle}</Badge>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {EXPORT_STATUS_LABEL[exportPanel.streamStatus]}
                </span>
              )}
            </div>
            {isReadyToGrant && exportPanel.resultDatasetId ? (
              exportedDatasetError ? (
                <span className="text-sm italic text-muted-foreground">Dataset unavailable</span>
              ) : (
                <PreviewRow
                  items={[
                    {
                      key: exportPanel.resultDatasetId,
                      label: exportedDataset?.title ?? exportPanel.resultDatasetId,
                      leading: (
                        <Folder className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      ),
                      to: nextGenDatasetDetailPath(exportPanel.resultDatasetId),
                    },
                  ]}
                />
              )
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Generate the dataset before you approve access.
                </p>
                <ChildDatasetExportPanel
                  request={request}
                  panel={exportPanel}
                  onDatasetNameChange={onDatasetNameChange}
                  onExport={onExport}
                />
              </>
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <StepMarker step={2} />
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-foreground">Confirm approval & share</p>
              {!isReadyToGrant && (
                <Badge variant="warning">
                  <CirclePause aria-hidden />
                  Waiting on export
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Approve the request &amp; grant access to the dataset to{" "}
              <span className="font-semibold text-foreground">{request.created_by}</span>.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="lg"
            className="min-w-40"
            onClick={onCancel}
            disabled={isExportStreaming}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className={APPROVE_BUTTON_CLASS}
            loading={isApproving}
            onClick={onApprove}
            disabled={!isReadyToGrant || isApproving}
          >
            Confirm approval &amp; share
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
